import { buildModoocarDetailUrl } from '@/app/(makers)/modoocar/rank/_features/rank'
import { isMacarongAppUserAgent } from '@/shared/lib'

import {
  ANNUAL_DISTANCE_KM,
  MAN_WON,
  MODOOCAR_BASE_URL,
  MONTHS_PER_YEAR,
  MYCLE_HOME_URL,
} from '../_constants'
import { calcOfficialRatio } from './realFuel'

import type { IModoocarQuery } from '../_apis'
import type { IAnnualFuelCostResult, IModoocarDetail } from '../_types'
import type { TVehicleType } from '@/app/(makers)/modoocar/_types'
import type { Metadata } from 'next'

export * from './realFuel'

/** [주의]: useIsInApp 클라이언트 판정과 같은 키를 써야 합니다. 다르면 서버와 클라이언트의 인앱 판정이 어긋납니다. */
const WEBVIEW_PREVIEW_PARAM_VALUE = '1'

/** [앱]: UA에 'Macarong'이 있으면 실제 인앱이고, `?webview=1`이 있으면 QA 미리보기입니다. */
export const detectInAppFromRequest = (
  userAgent: string | null,
  webviewParam: string | undefined
): boolean =>
  isMacarongAppUserAgent(userAgent ?? '') ||
  webviewParam === WEBVIEW_PREVIEW_PARAM_VALUE

const DESCRIPTION_MAX_LENGTH = 160

const truncate = (text: string, maxLength: number) =>
  text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text

/** [주의]: 사이트맵·내부 링크와 같은 URL이어야 합니다. 다르면 canonical이 어긋나 색인이 나뉩니다. */
export const buildModoocarCanonical = (
  detail: IModoocarDetail,
  query?: IModoocarQuery
): string => {
  if (query?.brandName && query.modelName) {
    const vehicleType: TVehicleType = detail.isMotorcycle ? 'MOTORCYCLE' : 'CAR'
    return buildModoocarDetailUrl({
      vehicleType,
      brandName: query.brandName,
      modelName: query.modelName,
      fuelType: query.fuelType,
    })
  }
  return `${MODOOCAR_BASE_URL}/${encodeURIComponent(detail.id)}`
}

const buildModoocarOgBase = (id: string) =>
  `${MODOOCAR_BASE_URL}/${encodeURIComponent(id)}`

/** [백엔드]: API(realUsageAnalysis)가 준 연간·월·1km당 비용을 먼저 쓰고, 없을 때만 유가로 계산합니다. */
export const calcAnnualFuelCost = ({
  realFuelEconomy,
  fuelPricePerLiter,
  annualDistanceKm = ANNUAL_DISTANCE_KM,
  annualWon: annualWonOverride,
  monthlyWon: monthlyWonOverride,
  perKmCost: perKmCostOverride,
}: {
  realFuelEconomy: number
  fuelPricePerLiter: number
  annualDistanceKm?: number
  annualWon?: number
  monthlyWon?: number
  perKmCost?: number
}): IAnnualFuelCostResult => {
  const hasApiAnnual = annualWonOverride != null && annualWonOverride > 0
  if (!hasApiAnnual && realFuelEconomy <= 0) {
    return { annualWon: 0, annualManWon: 0, monthlyManWon: 0, costPerKm: 0 }
  }

  const annualWon = hasApiAnnual
    ? annualWonOverride
    : (fuelPricePerLiter * annualDistanceKm) / realFuelEconomy
  const monthlyWon =
    monthlyWonOverride != null && monthlyWonOverride > 0
      ? monthlyWonOverride
      : annualWon / MONTHS_PER_YEAR

  return {
    annualWon: Math.round(annualWon),
    annualManWon: Math.round(annualWon / MAN_WON),
    monthlyManWon: Math.round(monthlyWon / MAN_WON),
    costPerKm:
      perKmCostOverride != null && perKmCostOverride > 0
        ? Math.round(perKmCostOverride)
        : Math.round(annualWon / annualDistanceKm),
  }
}

const buildBrandLogoUrl = (brandIcon: string, isMotorcycle: boolean) =>
  isMotorcycle
    ? `https://mycle.co.kr/resources/images/car/icon_bike_${brandIcon}_l.png`
    : `https://mycle.co.kr/resources/images/car/icon_car_${brandIcon}_l.png`

// [백엔드]: 리소스 서버 파일명에는 /가 없어서 지우고(`(F10/F11)`→`(F10F11)`), 한글 자소가 갈라지지 않도록 NFC로 맞춥니다.
const buildCarHubImageUrl = (company: string, carName: string) =>
  `https://mycle.co.kr/resources/images/car/img_report_car_top_${company.replace(/\//g, '')}_${carName.replace(/\//g, '')}.png`.normalize(
    'NFC'
  )

/** [주의]: og route.tsx와 같은 값이어야 합니다. 다르면 OG 이미지 크기가 메타 정보와 어긋납니다. */
const OG_IMAGE_WIDTH = 1200
const OG_IMAGE_HEIGHT = 630

const buildOgImageUrl = (detail: IModoocarDetail) => {
  const params = new URLSearchParams()
  params.append('car', buildCarHubImageUrl(detail.brand, detail.lowerModelName))
  if (detail.brandIcon)
    params.set(
      'logo',
      buildBrandLogoUrl(detail.brandIcon, Boolean(detail.isMotorcycle))
    )
  return `${buildModoocarOgBase(detail.id)}/og?${params.toString()}`
}

export const buildModoocarMetadata = (
  detail: IModoocarDetail,
  query?: IModoocarQuery
): Metadata => {
  const { brand, lowerModelName, realFuel, fuelCost, fuelType, ownerReview } =
    detail
  const isElectric = fuelType === '전기'
  const effLabel = isElectric ? '실전비' : '실연비'
  const effUnit = isElectric ? 'km/kWh' : 'km/L'
  const costLabel = isElectric ? '연 전기요금' : '연 유류비'
  const cost = calcAnnualFuelCost({
    realFuelEconomy: realFuel.realFuelEconomy,
    fuelPricePerLiter: fuelCost.fuelPricePerLiter,
    annualWon: fuelCost.annualFuelCostWon,
    monthlyWon: fuelCost.monthlyFuelCostWon,
  })

  const titleCostLabel = isElectric ? '전기요금' : '유류비'
  const title = `'${brand} ${lowerModelName}' 오너들의 ${effLabel} · ${titleCostLabel} 후기 | 모두의 차고`

  const description = truncate(
    `마이클 오너 9백만명의 ${lowerModelName} 실사용 기준, ${effLabel} ${realFuel.realFuelEconomy}${effUnit}, 만족도 ${ownerReview.averageScore.toFixed(2)}점, ${costLabel} 약 ${cost.annualManWon.toLocaleString()}만원이다.`,
    DESCRIPTION_MAX_LENGTH
  )

  const canonical = buildModoocarCanonical(detail, query)
  const ogImage = {
    url: buildOgImageUrl(detail),
    width: OG_IMAGE_WIDTH,
    height: OG_IMAGE_HEIGHT,
    alt: `${brand} ${lowerModelName}`,
  }

  return {
    title,
    description,
    alternates: { canonical },
    openGraph: {
      title,
      description,
      url: canonical,
      locale: 'ko_KR',
      type: 'website',
      images: [ogImage],
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      images: [ogImage.url],
    },
  }
}

// [주의]: <를 이스케이프합니다. 빼면 데이터에 든 </script>가 스크립트를 일찍 닫아 XSS가 생깁니다.
export const safeJsonLd = (data: unknown): string =>
  JSON.stringify(data).replace(/</g, '\\u003c')

export const buildVehicleJsonLd = (
  detail: IModoocarDetail,
  query?: IModoocarQuery
) => ({
  '@context': 'https://schema.org',
  '@type': 'Vehicle',
  name: `${detail.brand} ${detail.upperModelName}`,
  brand: { '@type': 'Brand', name: detail.brand },
  model: detail.upperModelName,
  fuelType: detail.fuelType,
  fuelConsumption: {
    '@type': 'QuantitativeValue',
    value: detail.realFuel.realFuelEconomy,
    unitText: 'km/L',
  },
  url: buildModoocarCanonical(detail, query),
})

export const buildBreadcrumbJsonLd = (
  detail: IModoocarDetail,
  query?: IModoocarQuery
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '마이클', item: MYCLE_HOME_URL },
    {
      '@type': 'ListItem',
      position: 2,
      name: '모두의 차고',
      item: MODOOCAR_BASE_URL,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: `${detail.brand} ${detail.upperModelName}`,
      item: buildModoocarCanonical(detail, query),
    },
  ],
})

export const buildFaqJsonLd = (detail: IModoocarDetail) => {
  const { brand, upperModelName, realFuel, fuelCost } = detail
  const cost = calcAnnualFuelCost({
    realFuelEconomy: realFuel.realFuelEconomy,
    fuelPricePerLiter: fuelCost.fuelPricePerLiter,
    annualWon: fuelCost.annualFuelCostWon,
    monthlyWon: fuelCost.monthlyFuelCostWon,
  })
  const officialSentence =
    realFuel.officialFuelEconomy > 0
      ? ` 국토교통부 공인연비 ${realFuel.officialFuelEconomy}km/L 대비 약 ${calcOfficialRatio(realFuel.realFuelEconomy, realFuel.officialFuelEconomy)}% 수준입니다.`
      : ''

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${upperModelName} 실연비는 얼마인가요?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${brand} ${upperModelName}의 실연비는 마이클 오너 ${realFuel.ownerCount.toLocaleString()}명 실측 기준 ${realFuel.realFuelEconomy}km/L입니다.${officialSentence} (${realFuel.basisYearMonth} 누적 실사용 기준)`,
        },
      },
      {
        '@type': 'Question',
        name: `${upperModelName} 연간 유류비는 얼마나 드나요?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `연 ${ANNUAL_DISTANCE_KM.toLocaleString()}km 주행 기준, 오피넷 ${fuelCost.fuelPriceYearMonth} 평균 유가 ${fuelCost.fuelPricePerLiter.toLocaleString()}원/L를 적용하면 ${upperModelName}의 연간 유류비는 약 ${cost.annualManWon.toLocaleString()}만원, 월 약 ${cost.monthlyManWon.toLocaleString()}만원입니다.`,
        },
      },
    ],
  }
}

/** [라이브러리]: React는 하이드레이션 전에 실패한 이미지의 onError를 호출하지 않아서, ref 콜백에서 이미 깨진 이미지(naturalWidth=0)를 찾습니다. */
export const isAlreadyBrokenImage = (node: HTMLImageElement | null) =>
  Boolean(node && node.complete && node.naturalWidth === 0)
